import React, { useCallback, useEffect, useRef, useState } from "react";

import { cameraService, galleryService } from "../../services";
import { useFileSizeFormatter } from "../../utils/formatters";
import { logger } from "../../utils/logger";

// Create component logger for ChatInput
const chatInputLogger = logger.withComponent("ChatInput");

interface ChatInputProps {
  onSend?: (text: string, files?: File[]) => void;
  isLoading?: boolean;
  autoOpenMenu?: boolean;
  inputVisibility?: "both" | "text" | "plus" | "none";
  autoTriggerAction?: "camera" | "gallery"; // Auto-trigger camera or gallery on mount
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  isLoading = false,
  autoOpenMenu = false,
  inputVisibility = "both",
  autoTriggerAction,
}) => {
  const [text, setText] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const [menuError, setMenuError] = useState("");
  const formatFileSize = useFileSizeFormatter();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const send = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed && attachedFiles.length === 0) return;

    if (typeof onSend === "function") {
      onSend(trimmed, attachedFiles.length > 0 ? attachedFiles : undefined);
    }

    setText("");
    setAttachedFiles([]);
  }, [text, attachedFiles, onSend]);

  const toggleMenu = useCallback(() => {
    setShowMenu(!showMenu);
    setMenuError("");

    // Focus first menu item when opening
    if (!showMenu) {
      setTimeout(() => {
        const firstMenuItem = menuRef.current?.querySelector(
          ".menu-item"
        ) as HTMLButtonElement;
        firstMenuItem?.focus();
      }, 0);
    }
  }, [showMenu]);

  const closeMenu = useCallback(() => {
    setShowMenu(false);
    setMenuError("");
  }, []);

  const handleMenuKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!showMenu) return;

      const menuItems = menuRef.current?.querySelectorAll(
        ".menu-item"
      ) as NodeListOf<HTMLButtonElement>;
      if (!menuItems || menuItems.length === 0) return;

      const currentIndex = Array.from(menuItems).findIndex(
        item => item === document.activeElement
      );

      switch (event.key) {
        case "Escape":
          event.preventDefault();
          closeMenu();
          break;
        case "ArrowDown":
          event.preventDefault();
          const nextIndex =
            currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
          menuItems[nextIndex].focus();
          break;
        case "ArrowUp":
          event.preventDefault();
          const prevIndex =
            currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
          menuItems[prevIndex].focus();
          break;
      }
    },
    [showMenu, closeMenu]
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const newFiles = Array.from(files);
      setAttachedFiles(prev => [...prev, ...newFiles]);
      closeMenu();

      // Clear the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [closeMenu]
  );

  const removeFile = useCallback((index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        send();
      }
    },
    [send]
  );

  const handleCameraCapture = useCallback(async () => {
    try {
      setMenuError("");
      const file = await cameraService.capture({
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.8,
      });

      setAttachedFiles(prev => [...prev, file]);
      closeMenu();
    } catch (error) {
      chatInputLogger.error("Camera capture error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Camera capture failed";
      if (errorMessage.includes("cancelled")) {
        setMenuError(__('Camera capture was cancelled'));
      } else {
        setMenuError(__('Camera access denied or not available'));
      }
    }
  }, [closeMenu]);

  const handleGallerySelect = useCallback(async () => {
    try {
      setMenuError("");
      const result = await galleryService.selectImages({
        multiple: true,
        maxSize: 10 * 1024 * 1024, // 10MB
        compressLargeImages: true,
        quality: 0.8,
      });

      if (result.files.length > 0) {
        setAttachedFiles(prev => [...prev, ...result.files]);
      }

      if (result.errors.length > 0) {
        setMenuError(`${__('Some files could not be added')}: ${result.errors[0]}`);
      }

      closeMenu();
    } catch (error) {
      chatInputLogger.error("Gallery selection error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Gallery selection failed";
      if (errorMessage.includes("cancelled")) {
        setMenuError(__('Gallery selection was cancelled'));
      } else {
        setMenuError(__('Failed to select images from gallery'));
      }
    }
  }, [closeMenu]);

  // Auto-trigger camera or gallery based on autoTriggerAction prop
  useEffect(() => {
    if (autoTriggerAction === "camera") {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        handleCameraCapture();
      }, 100);
      return () => clearTimeout(timer);
    } else if (autoTriggerAction === "gallery") {
      const timer = setTimeout(() => {
        handleGallerySelect();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoTriggerAction, handleCameraCapture, handleGallerySelect]);

  return (
    <div className="chat-input">
      {/* File attachments preview */}
      {attachedFiles.length > 0 && (
        <div className="attachment-preview">
          {attachedFiles.map((file, index) => (
            <div key={index} className="attachment-item">
              <span className="file-name">{file.name}</span>
              <span className="file-size">({formatFileSize(file.size)})</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="remove-file"
                aria-label={__('Remove file')}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="input-container">
        {/* Plus button for attachments */}
        {(inputVisibility === "both" || inputVisibility === "plus") && (
          <div className="plus-menu">
            <button
              type="button"
              onClick={toggleMenu}
              className="plus-button"
              disabled={isLoading}
              aria-label={__('Attach files')}
              aria-expanded={showMenu}
              aria-haspopup="menu"
            >
              +
            </button>

            {showMenu && (
              <div
                className="attachment-menu"
                ref={menuRef}
                onKeyDown={handleMenuKeyDown}
                role="menu"
                aria-label={__('Attachment options')}
              >
                <button
                  type="button"
                  onClick={handleGallerySelect}
                  className="menu-item"
                  aria-label={__('Select images from gallery')}
                  role="menuitem"
                >
                  {__('📎 Gallery')}
                </button>
                <button
                  type="button"
                  onClick={handleCameraCapture}
                  className="menu-item"
                  aria-label={__('Take photo with camera')}
                  role="menuitem"
                >
                  {__('📷 Camera')}
                </button>
                {menuError && <div className="menu-error">{menuError}</div>}
              </div>
            )}
          </div>
        )}

        {/* Text input */}
        {(inputVisibility === "both" || inputVisibility === "text") && (
          <textarea
            ref={textareaRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={__('Type your message...')}
            disabled={isLoading}
            className="text-input"
            rows={1}
            aria-label={__('Type your message')}
          />
        )}

        {/* Send button */}
        <button
          type="button"
          onClick={send}
          disabled={isLoading || (!text.trim() && attachedFiles.length === 0)}
          className="send-button"
          aria-label={__('Send message')}
        >
          {isLoading ? "..." : "→"}
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        aria-hidden="true"
      />

      {/* Click outside to close menu */}
      {showMenu && (
        <div
          className="menu-overlay"
          onClick={closeMenu}
          aria-hidden="true"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
        />
      )}
    </div>
  );
};
